import { HttpClient } from '@angular/common/http';
import { digger } from '@cbre/digger';
import {
  concatValuesBy,
  defaultTxt,
  EMPTY_STRING,
  formatNumber,
  formatValueWithUom,
  getPropertyTypeLabel,
  simpleValueValidation,
  UserSettings,
} from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  LovValues,
  TemplateFieldsDisplayFn,
} from '../../../../../models/template.model';
import { sortAttributesByFieldId } from '../../../utils/argument-sorting';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import {
  currencyLov,
  PropertyType,
  propertyTypeDisplayFnFactory,
  RoleDescriptions,
  valueWithUomPrefix,
} from '../us.common';

interface PropertyForSale {
  forSale: boolean;
  price?: number;
  uom?: string;
}

const PIPE_SEPERATOR = ' | ';

export const US_PROPERTY_COMMON_HEADER_FIELDS: TemplateFieldsDisplayFn = {
  27: {
    // 27 - Property Name
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values).replace(/,(?=[^,]*$)/, ''); // Removing last comma before PIN
    },
    routeFn: ({ data }) => {
      const entityRecord = Array.isArray(data) ? data[0] : data;
      return ['/records', 'property', digger(entityRecord, 'entity.id'), 'details'];
    },
  },
  49: {
    // 49 - Property Address
    displayFn: (record: ReportRecord, _options, arg1) => {
      const values = concatValuesBy(defaultTxt, mapAndFormatDynamicValue(record, arg1));
      return values;
    },
  },
  128: { displayFn: displayFnWrapper }, // 128 - County
  129: { displayFn: displayFnWrapper }, // 129 - Property ID
  130: { displayFn: displayFnWrapper }, // 130 - Property Status
  131: {
    // 131 - Primary Property Type
    displayFn: (record: ReportRecord, options, arg: DisplayFnArgumentsInfo) => {
      if (arg.field != null && arg.field.includes('usages')) {
        const field = arg.field.split('usages/').join(`usages[primary_usage_f=true].`);
        arg = { ...arg, field };
        let values = displayFnWrapper(record, options, arg);
        return Array.isArray(values)
          ? values
              .map(v => getPropertyTypeLabel(v))
              .filter(Boolean)
              .join(', ') || defaultTxt
          : (getPropertyTypeLabel(values) ?? defaultTxt);
      }
    },
  },
  132: {
    // 132 - Bldg|Site size
    displayFn: (record: ReportRecord, _, building, site) => {
      const buildingValue = mapAndFormatDynamicValue(record, building);
      const siteValue = mapAndFormatDynamicValue(record, site);
      return concatValuesBy(' | ', buildingValue, siteValue);
    },
  },
  134: {
    // 134 - Built | Reno
    displayFn: (record: ReportRecord, _, built, reno) => {
      const builtValue = mapAndFormatDynamicValue(record, built);
      const renoValue = mapAndFormatDynamicValue(record, reno);
      return concatValuesBy(' | ', builtValue, renoValue);
    },
  },
  135: { displayFn: displayFnWrapper }, // 135 - Reno Year
};

export function US_PROPERTY_COMMON_BODY_FIELDS(propertyType: PropertyType): TemplateFieldsDisplayFn {
  const displayFnPropertyType = propertyTypeDisplayFnFactory(propertyType);
  function formatAvgDirectAskingRate(
    record: ReportRecord,
    options: DisplayFunctionOptions,
    rateType: DisplayFnArgumentsInfo,
    monthlyRate: DisplayFnArgumentsInfo,
    yearlyRate: DisplayFnArgumentsInfo
  ) {
    return currencyLov(options).pipe(
      map((currencyLOV: LovValues[]) => {
        const termToUse = UserSettings.isYearlyPreference() ? yearlyRate : monthlyRate;
        const rateVal: string | string[] = valueWithUomPrefix(record, propertyType, termToUse, currencyLOV);
        return concatValuesBy(defaultTxt, rateVal, displayFnPropertyType(record, undefined, rateType));
      })
    );
  }
  return {
    136: { displayFn: displayFnPropertyType }, // 136 - Total Available
    137: { displayFn: displayFnPropertyType }, // 137 - Total Vacant
    138: { displayFn: displayFnPropertyType }, // 138 - Available Direct
    139: { displayFn: displayFnPropertyType }, // 139 - Available Sublease
    140: { displayFn: displayFnPropertyType }, // 140 - Min Divisible
    141: { displayFn: displayFnPropertyType }, // 141 - Max Contiguous
    86: { displayFn: displayFnPropertyType }, // 86 - Parking Ratio
    143: { displayFn: formatAvgDirectAskingRate }, // 143 - Direct Rate
    144: { displayFn: formatAvgDirectAskingRate }, // 144 - Sublease Rate
    147: {
      // 147 - For Sale
      displayFn: (record: ReportRecord, options: { httpClient: HttpClient }) => {
        const { httpClient } = options;
        const data = Array.isArray(record.data) ? record.data[0] : record.data;
        const propertyId = data.entity.id;
        return httpClient.get(`/api/property/${propertyId}/forsale`).pipe(
          map((propertyForSale: PropertyForSale) => {
            const { forSale, price, uom } = propertyForSale;
            let response = forSale ? 'Yes ' : 'No';
            if (forSale) {
              const hasUom = uom != null;
              if (hasUom) {
                response += price != null ? formatValueWithUom({ value: price, uom }) : '';
              } else {
                response += price != null ? formatNumber(price, undefined) : '';
              }
            }
            return response;
          })
        );
      },
    },
    148: { displayFn: displayFnPropertyType }, // 148 - Tenancy Type
    149: { displayFn: displayFnPropertyType }, // 149 - Date Available
    150: { displayFn: displayFnPropertyType }, // 150 - Property Comments
    202: {
      // 202 - Listing Rep
      displayFn: (record: ReportRecord, _, arg) => {
        return formatCompanyContactByRole(record, arg, RoleDescriptions.ListingRep, PIPE_SEPERATOR);
      },
      isVisible: (_, settings) => {
        return !settings.reportOptions.brokerAgency;
      },
    },
  };
}

export function US_PROPERTY_COMMON_FOOTER_FIELDS(propertyType: PropertyType): TemplateFieldsDisplayFn {
  return { 151: { displayFn: propertyTypeDisplayFnFactory(propertyType) } }; // 151 - Subtype
}

export function mapFormatAndFilterValues(
  record: ReportRecord,
  fields: DisplayFnArgumentsInfo[],
  filterValue: string
): string[][] {
  return fields.map(field => (mapAndFormatDynamicValue(record, field) as string[])?.filter(val => val !== filterValue));
}

export function filterUsagesByPropertyType(record: ReportRecord, selectedTemplateType: string): ReportRecord {
  return {
    ...record,
    data: record?.data?.map(dataItem => {
      const usagesForSelectedTemplateType = dataItem?.usages?.filter(
        usage => usage?.ref_property_usage_type_desc === selectedTemplateType
      );
      let usages = [];
      if (usagesForSelectedTemplateType?.length > 0) {
        usages = usagesForSelectedTemplateType;
      } else if (dataItem?.usages.length > 0) {
        usages = dataItem.usages.slice(0, 1);
      }
      return { ...dataItem, usages };
    }),
  };
}

/**
 * Format Company/Contact records into string using company name and contact
 * display name.
 *
 * @param record
 * @param attributeInfo
 * @param requiredRole
 *
 * @example
 * Expected output from this method for a single tim record
 * CompanyName1 - Contact1,Contact2,Contact3
 * CompanyName2 - Contact2,Contact3,Contact4
 */
function formatCompanyContactByRole(
  record: ReportRecord,
  attributeInfo: DisplayFnArgumentsInfo,
  requiredRole: string,
  separator: string
): string {
  if (!simpleValueValidation(attributeInfo?.field)) {
    return defaultTxt;
  }
  const companyContactRecords = digger(record.data[0], 'usages/company_contact_role_addresses');
  const uniqueCompanyIds = [...new Set(companyContactRecords.map(item => item.company_id))];
  const formattedCompanyContacts: string[] = uniqueCompanyIds.map((companyId, _index) => {
    // filter company/contact record by id and role
    const filteredCompanyContacts = companyContactRecords.filter(
      itemContact => itemContact.company_id === companyId && itemContact.role_desc === requiredRole
    );
    let companyName: string;
    if (filteredCompanyContacts.length) {
      companyName = filteredCompanyContacts[0].party_organization_display_name;
      // join contact display names
      const contactsDisplayName = filteredCompanyContacts
        .filter(itemContact => itemContact.contact_id)
        .map(itemContact => itemContact.familiar_name || defaultTxt)
        .join(', ');
      return companyName ? `${companyName} ${separator} ${contactsDisplayName || defaultTxt}` : EMPTY_STRING;
    }
    return EMPTY_STRING;
  });
  return formattedCompanyContacts.length > 0
    ? formattedCompanyContacts.filter(data => data != EMPTY_STRING).join('\n')
    : defaultTxt;
}
